'use client';

import { AppImageClient, AppImageData } from '@components/appImage';

import type { MobileSdkMedia } from '../constants';

import Vector from '@/assets/icons/vectorBackground.svg';
import { Button } from '@/shared/components/button';
import { useCurrentLocale, useGetClientHost } from '@/shared/hooks';
import { TranslatedComponent } from '@/shared/types';
import { getDocsUrl } from '@/shared/utils/getDocsUrl';
import { getDomainKey } from '@/shared/utils/getDomainInfo';

interface Props extends TranslatedComponent {
  /** Скриншот навигатора по доменам, данные AppImage с сервера */
  mobileScreenImages: MobileSdkMedia;
  iconApple: AppImageData;
  iconAndroid: AppImageData;
  iconFlutter: AppImageData;
  /** Фоновая сетка мобильной версии */
  vectorBackground?: AppImageData;
}

export const MobileSdkSection = ({
  translations,
  mobileScreenImages,
  iconApple,
  iconAndroid,
  iconFlutter,
  vectorBackground,
}: Props) => {
  const domain = getDomainKey(useGetClientHost());
  const locale = useCurrentLocale();
  const mobileScreenImage = mobileScreenImages[domain];
  const sdkDocsHref = {
    ios: getDocsUrl('/ios/sdk/overview', locale),
    android: getDocsUrl('/android/sdk/overview', locale),
    flutter: getDocsUrl('/flutter/sdk/overview', locale),
  };

  return (
    <>
      {/** Desktop версия */}
      <div className="relative hidden md:block">
        <AppImageClient
          {...mobileScreenImage}
          alt="mobile-navigation-sdk"
          className="absolute bottom-0 left-[132px] h-[510px] w-[303px]"
        />
        <div className="relative grid h-[477px] grid-cols-[512px_auto] overflow-hidden rounded-[20px]">
          <div>
            <div
              className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 z-1"
              style={{
                background:
                  'linear-gradient(90deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.00) 100%)',
              }}
            />
            <div className="absolute left-[-132px] z-[-2] h-[513px] w-[1267px] opacity-10">
              <Vector />
            </div>
            <div
              className="absolute top-[28px] left-[76px] z-[-1] h-[414px] w-[414px]"
              style={{
                borderRadius: 414,
                background:
                  'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.00) 28.05%, rgba(68, 180, 60, 0.10) 100%)',
                backdropFilter: 'blur(39.62378692626953px)',
              }}
            />
            <div
              className="absolute top-[74px] left-[123px] z-[-1] h-[326px] w-[324px]"
              style={{
                borderRadius: 326,
                background:
                  'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.00) 54.56%, rgba(68, 180, 60, 0.10) 100%)',
                backdropFilter: 'blur(39.62378692626953px)',
              }}
            />
          </div>
          <div className="flex size-full flex-col justify-center gap-4 text-[18px] leading-[28px] tracking-[-0.18px]">
            <p>{translations.mobileSdkDescription1}</p>
            <p>{translations.mobileSdkDescription2}</p>
            <p>{translations.mobileSdkDescription3}</p>
            <div className="mt-4 flex items-center justify-between">
              <Button
                target="_blank"
                href="/api/mobile-sdk"
                variant="secondary"
                className="size-fit px-6 py-4"
              >
                {translations.buttonMoreDetails}
              </Button>
              <div className="flex gap-3">
                <Button target="_blank" href={sdkDocsHref.ios}>
                  <AppImageClient
                    {...iconApple}
                    alt="iOS"
                    className="h-[48px] w-[48px]"
                  />
                </Button>
                <Button target="_blank" href={sdkDocsHref.android}>
                  <AppImageClient
                    {...iconAndroid}
                    alt="Android"
                    className="h-[48px] w-[48px]"
                  />
                </Button>
                <Button target="_blank" href={sdkDocsHref.flutter}>
                  <AppImageClient
                    {...iconFlutter}
                    alt="Flutter"
                    className="h-[48px] w-[48px]"
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/** Мобильная версия */}
      <div className="flex h-auto flex-col overflow-hidden px-4 md:hidden">
        <div className="flex flex-col gap-2">
          <p className="text-[16px] leading-[24px] font-normal tracking-[-0.16px]">
            {translations.mobileSdkDescription1}
          </p>
          <p className="text-[16px] leading-[24px] font-normal tracking-[-0.16px]">
            {translations.mobileSdkDescription2}
          </p>
          <p className="text-[16px] leading-[24px] font-normal tracking-[-0.16px]">
            {translations.mobileSdkDescription3}
          </p>
          <div className="mt-4 flex items-center justify-between">
            <Button
              target="_blank"
              href="/api/mobile-sdk"
              variant="secondary"
              className="size-fit px-6 py-4"
            >
              {translations.buttonMoreDetails}
            </Button>
            <div className="flex gap-3">
              <Button target="_blank" href={sdkDocsHref.ios}>
                <AppImageClient
                  {...iconApple}
                  alt="iOS"
                  className="h-[48px] w-[48px]"
                />
              </Button>
              <Button target="_blank" href={sdkDocsHref.android}>
                <AppImageClient
                  {...iconAndroid}
                  alt="Android"
                  className="h-[48px] w-[48px]"
                />
              </Button>
              <Button target="_blank" href={sdkDocsHref.flutter}>
                <AppImageClient
                  {...iconFlutter}
                  alt="Flutter"
                  className="h-[48px] w-[48px]"
                />
              </Button>
            </div>
          </div>
        </div>
        <div className="rounded-b-20 relative mt-9 flex h-[400px] items-center justify-center overflow-hidden">
          {/** Сетка */}
          {vectorBackground && (
            <div className="absolute">
              <AppImageClient
                {...vectorBackground}
                alt=""
                className="h-auto w-full"
              />
            </div>
          )}
          {/** Темный бекграунд */}
          <div
            className="pointer-events-none absolute top-0 right-0 bottom-0 left-0"
            style={{
              background:
                'linear-gradient(0deg, rgba(255, 255, 255, 0.00) 0%, rgba(25, 32, 37, 0.9) 90%)',
            }}
          />
          {/** Белый бекграунд */}
          <div
            className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 opacity-20"
            style={{
              background:
                'linear-gradient(0deg, rgba(255, 255, 255, 0.48) 0%, rgba(255, 255, 255, 0.00) 90%)',
            }}
          />
          {/** Малый радиус */}
          <div
            className="absolute top-[80%] left-[50%] h-[315px] w-[315px] translate-x-[-50%] translate-y-[-50%]"
            style={{
              borderRadius: 245,
              background:
                'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.00) 28.05%, rgba(68, 180, 60, 0.10) 100%)',
              backdropFilter: 'blur(39.62378692626953px)',
            }}
          />
          {/** Большой радиус */}
          <div
            className="left-[50%]h-[245px] absolute top-[80%] w-[245px] translate-x-[-50%] translate-y-[-50%]"
            style={{
              borderRadius: 314,
              background:
                'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.00) 54.56%, rgba(68, 180, 60, 0.10) 100%)',
              backdropFilter: 'blur(39.62378692626953px)',
            }}
          />
          {/** Смартфон */}
          <AppImageClient
            {...mobileScreenImage}
            alt="mobile-navigation-sdk"
            className="relative h-[400px] w-auto"
          />
        </div>
      </div>
    </>
  );
};
